import PubSub from "pubsub-js"

const observeButtonRemoval = (
  selectedButton,
  removeExistingGizmo,
  editorContainer,
  handleScroll
) => {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      // Check if the selected button is removed
      if (mutation.type === "childList") {
        mutation.removedNodes.forEach((removedNode) => {
          if (removedNode === selectedButton) {
            removeExistingGizmo()
            editorContainer.removeEventListener("scroll", handleScroll)
            observer.disconnect() // Stop observing after the button is removed
          }
        })
      }
    }
  })

  // Start observing the parent node of the selected button
  if (selectedButton.parentNode) {
    observer.observe(selectedButton.parentNode, { childList: true })
  }
}

const observeButtonChanges = (selectedButton, gizmo) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(() => {
      updateGizmoPosition(gizmo, selectedButton)
    })
  })

  observer.observe(selectedButton, {
    attributes: true,
    attributeFilter: ["style", "class"], // Only watch for style and class changes
    childList: false,
    subtree: false,
  })

  return observer
}

const ButtonTransformGizmo = (selectedButton) => {
  if (!selectedButton) return

  // Remove any existing gizmos first
  removeExistingGizmo()

  // Create gizmo container
  const gizmo = document.createElement("div")
  gizmo.className =
    "ae-button-transform-gizmo absolute border-2 border-admin_primary pointer-events-none"
  gizmo.style.position = "absolute"
  gizmo.style.zIndex = "900"
  gizmo.style.pointerEvents = "none"

  // Add resize handles to corners and edges
  const handlePositions = [
    "top-left",
    "top",
    "top-right",
    "left",
    "right",
    "bottom-left",
    "bottom",
    "bottom-right",
  ]

  handlePositions.forEach((position) => {
    const handle = document.createElement("div")
    handle.className = `
      absolute w-3 h-3 bg-admin_paper border-2 border-admin_primary rounded-sm
      cursor-${getCursorStyle(position)} pointer-events-auto
    `

    // Position the handle
    switch (position) {
      case "top-left":
        handle.style.top = "-6px"
        handle.style.left = "-6px"
        break
      case "top":
        handle.style.top = "-6px"
        handle.style.left = "50%"
        handle.style.transform = "translateX(-50%)"
        break
      case "top-right":
        handle.style.top = "-6px"
        handle.style.right = "-6px"
        break
      case "left":
        handle.style.left = "-6px"
        handle.style.top = "50%"
        handle.style.transform = "translateY(-50%)"
        break
      case "right":
        handle.style.right = "-6px"
        handle.style.top = "50%"
        handle.style.transform = "translateY(-50%)"
        break
      case "bottom-left":
        handle.style.bottom = "-6px"
        handle.style.left = "-6px"
        break
      case "bottom":
        handle.style.bottom = "-6px"
        handle.style.left = "50%"
        handle.style.transform = "translateX(-50%)"
        break
      case "bottom-right":
        handle.style.bottom = "-6px"
        handle.style.right = "-6px"
        break
    }

    handle.addEventListener("mousedown", (e) => {
      e.stopPropagation()
      initResize(e, selectedButton, position)
    })

    gizmo.appendChild(handle)
  })

  const editorContainer = document.querySelector(".editor")

  // Add scroll event listener
  const handleScroll = () => {
    requestAnimationFrame(() => {
      updateGizmoPosition(gizmo, selectedButton)
    })
  }
  editorContainer.addEventListener("scroll", handleScroll)

  // Set up mutation observers
  const styleObserver = observeButtonChanges(selectedButton, gizmo)
  const removalObserver = observeButtonRemoval(
    selectedButton,
    () => {
      removeExistingGizmo()
      styleObserver.disconnect()
    },
    editorContainer,
    handleScroll
  )

  // Initial position
  updateGizmoPosition(gizmo, selectedButton)

  // Add gizmo to editor container
  editorContainer.appendChild(gizmo)

  // Store reference to this gizmo for global access
  window.__transformGizmo = {
    selectedElement: selectedButton,
    gizmo: gizmo,
    cleanup: () => {
      removeExistingGizmo()
      document.removeEventListener("mousedown", handleClickOutside)
      editorContainer.removeEventListener("scroll", handleScroll)
      styleObserver && styleObserver.disconnect()
      removalObserver && removalObserver.disconnect()
      window.__transformGizmo = null
    }
  }

  // Handle clicks outside
  const handleClickOutside = (e) => {
    // Don't remove gizmo if clicking on:
    // 1. The gizmo itself
    // 2. The selected button
    // 3. The horizontal options menu
    // 4. Any dropdown panels (portals)
    // 5. Any tooltips
    // 6. Property drawer/editor UI

    if (gizmo.contains(e.target)) return
    if (e.target === selectedButton) return

    // Check if property editor is active - don't remove gizmo
    if (window._isPropertyEditorActive) {
      return
    }

    // Check if click is inside horizontal button options menu
    const horizontalMenu = document.querySelector('.ae-horizontal-button-options-menu-container')
    if (horizontalMenu && horizontalMenu.contains(e.target)) return

    // Check if click is inside property drawer
    const propertyDrawer = e.target.closest('[data-testid="modal"], .modal-overlay, .modal-container')
    if (propertyDrawer) {
      return
    }

    // Check if click is inside any dropdown panels
    const dropdownPanels = document.querySelectorAll('[role="menu"], .ae-dropdown-panel')
    for (let panel of dropdownPanels) {
      if (panel.contains(e.target)) return
    }

    // Check if click is inside any tooltip
    const tooltips = document.querySelectorAll('[class*="tooltip"], [data-tooltip]')
    for (let tooltip of tooltips) {
      if (tooltip.contains(e.target)) return
    }

    // If we get here, it's a click outside - remove the gizmo
    removeExistingGizmo()
    document.removeEventListener("mousedown", handleClickOutside)
    editorContainer.removeEventListener("scroll", handleScroll)
    styleObserver && styleObserver.disconnect()
    removalObserver && removalObserver.disconnect()
    window.__transformGizmo = null
  }

  document.addEventListener("mousedown", handleClickOutside)

  // Set up resize observer for the editor container
  const resizeObserver = new ResizeObserver(() => {
    requestAnimationFrame(() => {
      updateGizmoPosition(gizmo, selectedButton)
    })
  })

  resizeObserver.observe(editorContainer)

  // Subscribe to content updates to refresh gizmo position
  // This handles cases where CSS changes (borders, padding, etc.) affect element dimensions
  const contentUpdateToken = PubSub.subscribe("html-editor:content-update", () => {
    // Use requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      updateGizmoPosition(gizmo, selectedButton)
    })
  })

  // Return cleanup function
  return () => {
    removeExistingGizmo()
    document.removeEventListener("mousedown", handleClickOutside)
    editorContainer.removeEventListener("scroll", handleScroll)
    styleObserver.disconnect()
    removalObserver.disconnect()
    resizeObserver.disconnect()
    PubSub.unsubscribe(contentUpdateToken)
    window.__transformGizmo = null
  }
}

// Helper function to get appropriate cursor style
const getCursorStyle = (position) => {
  switch (position) {
    case "top-left":
    case "bottom-right":
      return "nw-resize"
    case "top-right":
    case "bottom-left":
      return "ne-resize"
    case "top":
    case "bottom":
      return "ns-resize"
    case "left":
    case "right":
      return "ew-resize"
    default:
      return "move"
  }
}

// Helper function to remove existing gizmo
const removeExistingGizmo = () => {
  const existingGizmo = document.querySelector(".ae-button-transform-gizmo")
  if (existingGizmo) {
    existingGizmo.remove()

    // Double-check removal after a short delay
    setTimeout(() => {
      const stillExists = document.querySelector(".ae-button-transform-gizmo")
      if (stillExists) {
        stillExists.remove()
      }
    }, 50)
  }
}

// Helper function to update gizmo position
const updateGizmoPosition = (gizmo, button) => {
  const editorContainer = document.querySelector(".editor")
  if (!editorContainer) return

  const editorRect = editorContainer.getBoundingClientRect()
  const buttonRect = button.getBoundingClientRect()

  // Calculate position relative to the editor container
  const top = buttonRect.top - editorRect.top + editorContainer.scrollTop
  const left = buttonRect.left - editorRect.left + editorContainer.scrollLeft

  gizmo.style.left = `${left}px`
  gizmo.style.top = `${top}px`
  gizmo.style.width = `${buttonRect.width}px`
  gizmo.style.height = `${buttonRect.height}px`
}

// Initialize resize functionality
const initResize = (e, button, position) => {
  e.preventDefault()
  const startX = e.clientX
  const startY = e.clientY

  const startWidth = button.offsetWidth
  const startHeight = button.offsetHeight

  const onMouseMove = (moveEvent) => {
    moveEvent.preventDefault()
    const deltaX = moveEvent.clientX - startX
    const deltaY = moveEvent.clientY - startY
    let newWidth = startWidth
    let newHeight = startHeight

    // Calculate new dimensions based on handle position
    switch (position) {
      case "right":
        newWidth = Math.max(50, startWidth + deltaX)
        break
      case "left":
        newWidth = Math.max(50, startWidth - deltaX)
        break
      case "bottom":
        newHeight = Math.max(30, startHeight + deltaY)
        break
      case "top":
        newHeight = Math.max(30, startHeight - deltaY)
        break
      case "bottom-right":
        newWidth = Math.max(50, startWidth + deltaX)
        newHeight = Math.max(30, startHeight + deltaY)
        break
      case "bottom-left":
        newWidth = Math.max(50, startWidth - deltaX)
        newHeight = Math.max(30, startHeight + deltaY)
        break
      case "top-right":
        newWidth = Math.max(50, startWidth + deltaX)
        newHeight = Math.max(30, startHeight - deltaY)
        break
      case "top-left":
        newWidth = Math.max(50, startWidth - deltaX)
        newHeight = Math.max(30, startHeight - deltaY)
        break
    }

    // Apply size changes to the button
    button.style.width = `${Math.round(newWidth)}px`
    button.style.height = `${Math.round(newHeight)}px`

    // Remove any existing width/height classes
    const widthClass = Array.from(button.classList).find((cls) =>
      cls.startsWith("w-")
    )
    if (widthClass) {
      button.classList.remove(widthClass)
    }

    const heightClass = Array.from(button.classList).find((cls) =>
      cls.startsWith("h-")
    )
    if (heightClass) {
      button.classList.remove(heightClass)
    }

    // Update gizmo position
    const gizmo = document.querySelector(".ae-button-transform-gizmo")
    if (gizmo) {
      updateGizmoPosition(gizmo, button)
    }
  }

  const onMouseUp = () => {
    document.removeEventListener("mousemove", onMouseMove)
    document.removeEventListener("mouseup", onMouseUp)
  }

  document.addEventListener("mousemove", onMouseMove)
  document.addEventListener("mouseup", onMouseUp)
}

export default ButtonTransformGizmo
export { removeExistingGizmo, updateGizmoPosition }